import React from 'react';
import { FunctionField, useRecordContext } from 'react-admin';
import { DisplayHelpers } from 'utils/helpers';
import { calcPayoutRate } from 'resources/rate_settings/rateSettingsCalculations';
import { getMarginPercent } from './tabs/PositionRateSettingsTab.utils';
import {
  RateSettingsDisplayHelper,
  type RateClassificationT,
} from 'utils/displayHelpers/rate-settings';

interface ProRateFieldProps {
  nestedOperator?: string;
  useBlendedRate?: boolean | null;
  isUsingLocationType?: boolean;
  applyLocationTypeMarkup?: boolean;
  excludeTenCentReduction?: boolean;
}

const ProRateField = ({
  nestedOperator,
  useBlendedRate,
  isUsingLocationType,
  applyLocationTypeMarkup,
  excludeTenCentReduction,
}: ProRateFieldProps) => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }
  const { settings, location, blended_settings_charge_rate, non_blended_settings_charge_rate } =
    record;

  const getChargeRateCents = () => {
    if (!isUsingLocationType) {
      return null;
    }

    const baseKey = nestedOperator.replace('.', '_');
    const markupSuffix = applyLocationTypeMarkup
      ? 'with_location_type_markup'
      : 'without_location_type_markup';
    const rateKey = `${baseKey}_${markupSuffix}`;

    const rateSource = useBlendedRate
      ? blended_settings_charge_rate
      : non_blended_settings_charge_rate;
    return rateSource?.[rateKey];
  };

  const chargeRateCents = getChargeRateCents();

  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings, location });
  const chargeRateCentsValue =
    chargeRateCents ||
    rateSettingsDisplayHelper.getChargeRateCents(nestedOperator as RateClassificationT);
  const rateSettingHash = rateSettingsDisplayHelper.getSettingsForClassification(nestedOperator);

  const chargeRateWithBlendedMarkup = useBlendedRate
    ? chargeRateCents || rateSettingsDisplayHelper.get12hRate(nestedOperator as RateClassificationT)
    : chargeRateCentsValue;

  if (chargeRateWithBlendedMarkup) {
    const marginPercent = getMarginPercent(settings, nestedOperator);
    const isUsing12hRates =
      useBlendedRate !== null ? useBlendedRate : rateSettingHash.use_blended_rate;

    const rates = isUsing12hRates ? 'blended_rates' : 'non_blended_rates';
    const rateSettings = rateSettingHash[rates] || {
      blended_rate_markup: 0,
      charge_max_daily_regular_hours: 0,
      charge_max_daily_overtime_hours: 0,
      charge_overtime_multiplier: 0,
      payout_max_daily_regular_hours: 0,
      payout_max_daily_overtime_hours: 0,
      payout_overtime_multiplier: 0,
      payout_double_overtime_multiplier: 0,
      charge_double_overtime_multiplier: 0,
    };

    const {
      charge_max_daily_regular_hours,
      charge_max_daily_overtime_hours,
      charge_overtime_multiplier,
      payout_max_daily_regular_hours,
      payout_max_daily_overtime_hours,
      payout_overtime_multiplier,
      payout_double_overtime_multiplier,
      charge_double_overtime_multiplier,
    } = rateSettings;

    const payoutRateObj = {
      chargeRate: chargeRateWithBlendedMarkup,
      margin_percent: marginPercent,
      charge_max_daily_regular_hours,
      charge_max_daily_ot_hours: charge_max_daily_overtime_hours,
      charge_overtime_multiplier,
      payout_max_daily_regular_hours,
      payout_max_daily_ot_hours: payout_max_daily_overtime_hours,
      payout_overtime_multiplier,
      payout_double_overtime_multiplier,
      charge_double_overtime_multiplier,
      hours: 12, // Assuming a default of 12 hours for calculation
      excludeTenCentReduction,
    };

    const { payoutRate } = calcPayoutRate(payoutRateObj);

    return <FunctionField render={() => DisplayHelpers.money(payoutRate)} />;
  }
  return null;
};

ProRateField.defaultProps = {
  label: 'Professional Rate',
  nestedOperator: 'ten99',
  useBlendedRate: null,
  isUsingLocationType: false,
  excludeTenCentReduction: false,
};

export default ProRateField;
